// Główny komponent kalkulatora parametrów gięcia tworzyw sztucznych
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Thermometer, 
  Clock, 
  AlertTriangle, 
  CheckCircle,
  History,
  Download,
  Play,
  Pause,
  Settings,
  Info,
  Zap,
  TrendingUp,
  BookOpen,
  Save,
  FileText,
  DollarSign
} from 'lucide-react';
import { 
  BENDING_MATERIALS, 
  BENDING_MACHINES,
  BENDING_DEFECTS,
  findMaterial,
  findMachine,
  getAvailableThicknesses
} from './BendingDatabase';
import { BendingPhysics } from './BendingPhysics';
import { BendingVisualization } from './BendingVisualization';
import { BendingHistory } from './BendingHistory';
import { generateTechnicalPDF } from '../../calculators/TechnicalCalculatorBase';
import type { BendingParameters, BendingResult, BendingMaterial } from './BendingDatabase';

export const BendingCalculator: React.FC = () => {
  // Stan formularza
  const [selectedMaterialType, setSelectedMaterialType] = useState<BendingMaterial['type']>('PMMA_XT');
  const [selectedThickness, setSelectedThickness] = useState<number>(5);
  const [selectedMaterialId, setSelectedMaterialId] = useState<string>('pmma_xt_5');
  const [bendLength, setBendLength] = useState<number>(600);
  const [bendAngle, setBendAngle] = useState<number>(90);
  const [bendRadius, setBendRadius] = useState<number>(2);
  const [selectedMachine, setSelectedMachine] = useState<string>('shannon_hrk_1200');
  const [ambientTemp, setAmbientTemp] = useState<number>(20);
  const [humidity, setHumidity] = useState<number>(50);
  
  // Stan wyników
  const [result, setResult] = useState<BendingResult | null>(null);
  const [energyStats, setEnergyStats] = useState<any>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showDefectAnalysis, setShowDefectAnalysis] = useState(false);
  const [selectedDefect, setSelectedDefect] = useState<string>('');
  
  // Stan historii
  const [historySuggestion, setHistorySuggestion] = useState<any>(null);
  const [recentHistory, setRecentHistory] = useState<any[]>([]);
  
  // Efekt dla aktualizacji materiału gdy zmienia się typ lub grubość
  useEffect(() => {
    const materials = BENDING_MATERIALS.filter(
      m => m.type === selectedMaterialType && 
      m.name.includes(`${selectedThickness}mm`)
    );
    
    if (materials.length > 0) {
      setSelectedMaterialId(materials[0].id);
    }
  }, [selectedMaterialType, selectedThickness]);
  
  // Efekt dla sugestii z historii
  useEffect(() => {
    if (selectedMaterialId) {
      const suggestion = BendingHistory.suggestParameters(
        selectedMaterialId,
        selectedThickness,
        bendAngle
      );
      setHistorySuggestion(suggestion);
      
      const history = BendingHistory.getHistoryForMaterial(
        selectedMaterialId,
        selectedThickness,
        5
      );
      setRecentHistory(history);
    }
  }, [selectedMaterialId, selectedThickness, bendAngle]);
  
  // Obliczanie parametrów z rozszerzonymi statystykami
  const calculateParameters = useCallback(() => {
    const params: BendingParameters = {
      materialId: selectedMaterialId,
      thickness: selectedThickness,
      bendLength,
      bendAngle,
      bendRadius,
      ambientTemp,
      humidity,
      machineId: selectedMachine
    };
    
    try {
      const calculatedResult = BendingPhysics.calculateBendingParameters(params);
      
      // Oblicz zużycie energii i koszty
      const energy = BendingPhysics.estimateEnergyConsumption(
        calculatedResult.heatingTime,
        calculatedResult.upperHeaterTemp,
        calculatedResult.lowerHeaterTemp,
        bendLength
      );
      
      // Rozszerzone statystyki energetyczne
      const machine = findMachine(selectedMachine);
      const powerConsumption = machine ? (machine.upperPower + machine.lowerPower) / 1000 : 3; // kW
      const heatingTimeHours = calculatedResult.heatingTime / 3600;
      const totalEnergy = powerConsumption * heatingTimeHours;
      const energyCost = totalEnergy * 0.65; // PLN/kWh (średnia cena prądu przemysłowego)
      
      // Dodatkowe statystyki produkcyjne
      const cyclesPerHour = Math.floor(3600 / calculatedResult.estimatedCycleTime);
      const dailyProduction = cyclesPerHour * 8; // 8h dziennie
      const dailyEnergyCost = energyCost * dailyProduction;
      
      setEnergyStats({
        powerConsumption,
        totalEnergy,
        energyCost,
        cyclesPerHour,
        dailyProduction,
        dailyEnergyCost,
        efficiency: Math.round((calculatedResult.heatingTime / calculatedResult.estimatedCycleTime) * 100)
      });
      
      setResult({
        ...calculatedResult,
        warnings: calculatedResult.warnings,
        recommendations: calculatedResult.recommendations
      });
      
      console.log('Obliczone parametry:', calculatedResult);
      console.log('Statystyki energetyczne:', energyStats);
      
    } catch (error) {
      console.error('Błąd podczas obliczeń:', error);
      alert('Wystąpił błąd podczas obliczeń. Sprawdź wprowadzone dane.');
    }
  }, [
    selectedMaterialId,
    selectedThickness,
    bendLength,
    bendAngle,
    bendRadius,
    ambientTemp,
    humidity,
    selectedMachine
  ]);
  
  // Generowanie PDF
  const generatePDF = () => {
    if (!result || !energyStats) return;
    
    const material = findMaterial(selectedMaterialId);
    const machine = findMachine(selectedMachine);
    
    const pdfParameters = {
      'Materiał': material?.name || 'Nieznany',
      'Grubość': `${selectedThickness} mm`,
      'Długość gięcia': `${bendLength} mm`,
      'Kąt gięcia': `${bendAngle}°`,
      'Promień gięcia': `${bendRadius} mm`,
      'Maszyna': machine?.name || 'Nieznana',
      'Temp. otoczenia': `${ambientTemp}°C`,
      'Wilgotność': `${humidity}%`
    };
    
    const pdfResults = {
      'Temp. grzałka górna': `${result.upperHeaterTemp}°C`,
      'Temp. grzałka dolna': `${result.lowerHeaterTemp}°C`,
      'Czas nagrzewania': `${result.heatingTime} s`,
      'Czas chłodzenia': `${result.coolingTime} s`,
      'Czas cyklu': `${result.estimatedCycleTime} s`,
      'Strefa grzania': `${result.heatingZoneWidth} mm`,
      'Kompensacja sprężynowania': `+${result.springBackCompensation}°`,
      'Moc pobierana': `${energyStats.powerConsumption.toFixed(2)} kW`,
      'Zużycie energii/cykl': `${energyStats.totalEnergy.toFixed(3)} kWh`,
      'Koszt energii/cykl': `${energyStats.energyCost.toFixed(2)} PLN`,
      'Wydajność': `${energyStats.cyclesPerHour} cykli/h`,
      'Produkcja dzienna': `${energyStats.dailyProduction} szt`,
      'Dzienny koszt energii': `${energyStats.dailyEnergyCost.toFixed(2)} PLN`
    };
    
    generateTechnicalPDF(
      'Kalkulator Parametrów Gięcia',
      'giecie',
      pdfParameters,
      pdfResults,
      {
        warnings: result.warnings,
        tips: result.recommendations,
        safetyNotes: [
          'Używaj rękawic termoodpornych',
          'Zachowaj odstęp bezpieczeństwa od grzałek',
          'Kontroluj temperaturę pirometrem',
          'Nie przekraczaj maksymalnej temperatury materiału'
        ]
      }
    );
  };
  
  // Zapisywanie do historii
  const saveToHistory = (resultType: 'success' | 'failure' | 'partial') => {
    if (!result || !energyStats) return;
    
    BendingHistory.addEntry({
      materialId: selectedMaterialId,
      thickness: selectedThickness,
      bendLength,
      bendAngle,
      bendRadius,
      machineId: selectedMachine,
      parameters: {
        upperHeaterTemp: result.upperHeaterTemp,
        lowerHeaterTemp: result.lowerHeaterTemp,
        heatingTime: result.heatingTime,
        heatingZoneWidth: result.heatingZoneWidth,
        coolingTime: result.coolingTime
      },
      result: resultType,
      defects: resultType === 'failure' && selectedDefect ? [selectedDefect] : undefined,
      notes: '',
      operator: 'Operator',
      energyUsed: energyStats.totalEnergy
    });
    
    alert(`Proces zapisany jako ${resultType === 'success' ? 'udany' : resultType === 'failure' ? 'nieudany' : 'częściowo udany'}`);
    
    // Odśwież historię
    const history = BendingHistory.getHistoryForMaterial(
      selectedMaterialId,
      selectedThickness,
      5
    );
    setRecentHistory(history);
  };
  
  // Eksport do CSV
  const exportHistory = () => {
    const csv = BendingHistory.exportToCSV();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `historia_giecia_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };
  
  // Analiza defektu
  const analyzeDefect = () => {
    if (!selectedDefect || !result) return;
    
    const analysis = BendingPhysics.analyzeDefect(selectedDefect, result);
    
    if (analysis.defect) {
      alert(
        `Defekt: ${analysis.defect.name}\n\n` +
        `Przyczyna: ${analysis.defect.cause}\n\n` +
        `Rozwiązanie: ${analysis.defect.solution}\n\n` +
        analysis.suggestedParams.upperHeaterTemp
          ? `Sugerowane parametry:\n` +
            `- Temp. górna: ${analysis.suggestedParams.upperHeaterTemp}°C\n` +
            `- Temp. dolna: ${analysis.suggestedParams.lowerHeaterTemp}°C\n` +
            `- Czas grzania: ${analysis.suggestedParams.heatingTime}s`
          : ''
      );
    }
  };
  
  // Użyj sugestii z historii
  const useSuggestion = () => {
    if (historySuggestion && historySuggestion.suggested) {
      // Ustaw sugerowane parametry jako wynik
      setResult({
        upperHeaterTemp: historySuggestion.upperTemp,
        lowerHeaterTemp: historySuggestion.lowerTemp,
        heatingTime: historySuggestion.heatingTime,
        heatingZoneWidth: selectedThickness * 3, // domyślnie
        coolingTime: selectedThickness * 10, // domyślnie
        springBackCompensation: bendAngle * 0.15, // domyślnie
        estimatedCycleTime: historySuggestion.heatingTime + selectedThickness * 10 + 10,
        warnings: [`ℹ️ Parametry oparte na ${historySuggestion.basedOn} poprzednich procesach`],
        recommendations: [`✅ Zaufanie: ${historySuggestion.confidence}%`]
      });
      
      // Oblicz statystyki energetyczne dla sugerowanych parametrów
      const machine = findMachine(selectedMachine);
      const powerConsumption = machine ? (machine.upperPower + machine.lowerPower) / 1000 : 3;
      const heatingTimeHours = historySuggestion.heatingTime / 3600;
      const totalEnergy = powerConsumption * heatingTimeHours;
      const energyCost = totalEnergy * 0.65;
      const cycleTime = historySuggestion.heatingTime + selectedThickness * 10 + 10;
      const cyclesPerHour = Math.floor(3600 / cycleTime);
      
      setEnergyStats({
        powerConsumption,
        totalEnergy,
        energyCost,
        cyclesPerHour,
        dailyProduction: cyclesPerHour * 8,
        dailyEnergyCost: energyCost * cyclesPerHour * 8,
        efficiency: Math.round((historySuggestion.heatingTime / cycleTime) * 100)
      });
    }
  };
  
  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Nagłówek */}
      <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg p-6 shadow-xl">
        <h1 className="text-3xl font-bold mb-2">🔥 Kalkulator Parametrów Gięcia</h1>
        <p className="text-orange-100">
          Precyzyjny dobór parametrów dla maszyn Shannon HRK i GT z analizą kosztów
        </p>
      </div>
      
      {/* Formularz parametrów */}
      <div className="bg-zinc-800 rounded-lg shadow-xl p-6 border border-zinc-700">
        <h2 className="text-xl font-semibold mb-4 text-white">Parametry gięcia</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Wybór materiału */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Typ materiału
            </label>
            <select
              value={selectedMaterialType}
              onChange={(e) => setSelectedMaterialType(e.target.value as BendingMaterial['type'])}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="PMMA_XT">Plexi XT (wytłaczane)</option>
              <option value="PMMA_GS">Plexi GS (lane)</option>
              <option value="PC">Poliwęglan</option>
              <option value="PETG">PETG</option>
              <option value="PVC_FOAM">PVC spienione</option>
            </select>
          </div>
          
          {/* Grubość */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Grubość (mm)
            </label>
            <select
              value={selectedThickness}
              onChange={(e) => setSelectedThickness(Number(e.target.value))}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              {getAvailableThicknesses(selectedMaterialType).map(thickness => (
                <option key={thickness} value={thickness}>
                  {thickness} mm
                </option>
              ))}
            </select>
          </div>
          
          {/* Długość gięcia */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Długość linii gięcia (mm)
            </label>
            <input
              type="number"
              value={bendLength}
              onChange={(e) => setBendLength(Number(e.target.value))}
              min={10}
              max={2000}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          {/* Kąt gięcia */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Kąt gięcia (°)
            </label>
            <input
              type="number"
              value={bendAngle}
              onChange={(e) => setBendAngle(Number(e.target.value))}
              min={0}
              max={180}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          {/* Promień gięcia */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Promień gięcia (mm)
            </label>
            <select
              value={bendRadius}
              onChange={(e) => setBendRadius(Number(e.target.value))}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value={1}>Ostre (1mm)</option>
              <option value={2}>Standardowe (2mm)</option>
              <option value={5}>Łagodne (5mm)</option>
              <option value={10}>Bardzo łagodne (10mm)</option>
              <option value={20}>Promień duży (20mm)</option>
            </select>
          </div>
          
          {/* Maszyna */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Maszyna
            </label>
            <select
              value={selectedMachine}
              onChange={(e) => setSelectedMachine(e.target.value)}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              {BENDING_MACHINES.map(machine => (
                <option key={machine.id} value={machine.id}>
                  {machine.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Temperatura otoczenia */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Temperatura otoczenia (°C)
            </label>
            <input
              type="number"
              value={ambientTemp}
              onChange={(e) => setAmbientTemp(Number(e.target.value))}
              min={5}
              max={40}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          {/* Wilgotność */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Wilgotność (%)
            </label>
            <input
              type="number"
              value={humidity}
              onChange={(e) => setHumidity(Number(e.target.value))}
              min={20}
              max={90}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>
        
        {/* Przyciski akcji */}
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={calculateParameters}
            className="px-6 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors flex items-center gap-2"
          >
            <Settings className="w-4 h-4" />
            Oblicz parametry
          </button>
          
          {historySuggestion?.suggested && (
            <button
              onClick={useSuggestion}
              className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center gap-2"
            >
              <TrendingUp className="w-4 h-4" />
              Użyj sugestii ({historySuggestion.confidence}%)
            </button>
          )}
          
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="px-6 py-2 bg-zinc-600 text-white rounded-md hover:bg-zinc-500 transition-colors flex items-center gap-2"
          >
            <History className="w-4 h-4" />
            Historia
          </button>
          
          <button
            onClick={() => setShowDefectAnalysis(!showDefectAnalysis)}
            className="px-6 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors flex items-center gap-2"
          >
            <AlertTriangle className="w-4 h-4" />
            Analiza defektów
          </button>
        </div>
      </div>
      
      {/* Wyniki obliczeń */}
      {result && (
        <div className="bg-zinc-800 rounded-lg shadow-xl p-6 border border-zinc-700">
          <h2 className="text-xl font-semibold mb-4 text-white">Wyliczone parametry</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {/* Temperatury */}
            <div className="bg-red-900/20 border border-red-800/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Thermometer className="w-5 h-5 text-red-400" />
                <h3 className="font-semibold text-gray-200">Temperatury</h3>
              </div>
              <div className="space-y-1">
                <p className="text-sm">
                  <span className="text-gray-400">Grzałka górna:</span>
                  <span className="font-bold text-red-400 ml-2">{result.upperHeaterTemp}°C</span>
                </p>
                <p className="text-sm">
                  <span className="text-gray-400">Grzałka dolna:</span>
                  <span className="font-bold text-red-300 ml-2">{result.lowerHeaterTemp}°C</span>
                </p>
              </div>
            </div>
            
            {/* Czasy */}
            <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-blue-400" />
                <h3 className="font-semibold text-gray-200">Czasy procesu</h3>
              </div>
              <div className="space-y-1">
                <p className="text-sm">
                  <span className="text-gray-400">Nagrzewanie:</span>
                  <span className="font-bold text-blue-400 ml-2">{result.heatingTime}s</span>
                </p>
                <p className="text-sm">
                  <span className="text-gray-400">Chłodzenie:</span>
                  <span className="font-bold text-blue-300 ml-2">{result.coolingTime}s</span>
                </p>
                <p className="text-sm">
                  <span className="text-gray-400">Całkowity:</span>
                  <span className="font-bold text-blue-500 ml-2">{result.estimatedCycleTime}s</span>
                </p>
              </div>
            </div>
            
            {/* Parametry geometryczne */}
            <div className="bg-green-900/20 border border-green-800/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Settings className="w-5 h-5 text-green-400" />
                <h3 className="font-semibold text-gray-200">Parametry</h3>
              </div>
              <div className="space-y-1">
                <p className="text-sm">
                  <span className="text-gray-400">Strefa grzania:</span>
                  <span className="font-bold text-green-400 ml-2">{result.heatingZoneWidth}mm</span>
                </p>
                <p className="text-sm">
                  <span className="text-gray-400">Kompensacja:</span>
                  <span className="font-bold text-green-300 ml-2">+{result.springBackCompensation}°</span>
                </p>
              </div>
            </div>
          </div>
          
          {/* Statystyki energetyczne i kosztowe */}
          {energyStats && (
            <div className="bg-yellow-900/20 border border-yellow-800/30 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-5 h-5 text-yellow-400" />
                <h3 className="font-semibold text-yellow-300">Statystyki energetyczne i produkcyjne</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <div>
                  <p className="text-xs text-gray-400">Moc pobierana</p>
                  <p className="font-bold text-yellow-400">{energyStats.powerConsumption.toFixed(2)} kW</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Energia/cykl</p>
                  <p className="font-bold text-yellow-400">{energyStats.totalEnergy.toFixed(3)} kWh</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Koszt/cykl</p>
                  <p className="font-bold text-green-400">{energyStats.energyCost.toFixed(2)} PLN</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Wydajność</p>
                  <p className="font-bold text-blue-400">{energyStats.cyclesPerHour} cykli/h</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Produkcja/dzień</p>
                  <p className="font-bold text-purple-400">{energyStats.dailyProduction} szt</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Koszt dzienny</p>
                  <p className="font-bold text-red-400">{energyStats.dailyEnergyCost.toFixed(2)} PLN</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-yellow-800/30">
                <p className="text-sm text-gray-300">
                  Efektywność energetyczna procesu: 
                  <span className="font-bold text-yellow-400 ml-2">{energyStats.efficiency}%</span>
                </p>
              </div>
            </div>
          )}
          
          {/* Ostrzeżenia */}
          {result.warnings.length > 0 && (
            <div className="bg-orange-900/20 border-l-4 border-orange-500 p-4 mb-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-orange-300 mb-1">Ostrzeżenia</h4>
                  <ul className="space-y-1">
                    {result.warnings.map((warning, index) => (
                      <li key={index} className="text-sm text-orange-200">
                        {warning}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          {/* Rekomendacje */}
          {result.recommendations.length > 0 && (
            <div className="bg-blue-900/20 border-l-4 border-blue-500 p-4 mb-4">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-300 mb-1">Rekomendacje</h4>
                  <ul className="space-y-1">
                    {result.recommendations.map((rec, index) => (
                      <li key={index} className="text-sm text-blue-200">
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          {/* Przyciski zapisu i eksportu */}
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              onClick={generatePDF}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Generuj PDF
            </button>
            
            <button
              onClick={() => saveToHistory('success')}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              Proces udany
            </button>
            
            <button
              onClick={() => saveToHistory('partial')}
              className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors flex items-center gap-2"
            >
              <AlertTriangle className="w-4 h-4" />
              Częściowo udany
            </button>
            
            <button
              onClick={() => saveToHistory('failure')}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center gap-2"
            >
              <AlertTriangle className="w-4 h-4" />
              Nieudany
            </button>
            
            <button
              onClick={() => setIsAnimating(!isAnimating)}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center gap-2"
            >
              {isAnimating ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {isAnimating ? 'Zatrzymaj' : 'Animuj'} gięcie
            </button>
          </div>
        </div>
      )}
      
      {/* Wizualizacja */}
      {result && (
        <BendingVisualization
          materialId={selectedMaterialId}
          thickness={selectedThickness}
          bendAngle={bendAngle}
          bendRadius={bendRadius}
          upperTemp={result.upperHeaterTemp}
          lowerTemp={result.lowerHeaterTemp}
          heatingTime={result.heatingTime}
          heatingZoneWidth={result.heatingZoneWidth}
          isAnimating={isAnimating}
        />
      )}
      
      {/* Panel historii */}
      {showHistory && (
        <div className="bg-zinc-800 rounded-lg shadow-xl p-6 border border-zinc-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">Historia procesów</h2>
            <button
              onClick={exportHistory}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Eksportuj CSV
            </button>
          </div>
          
          {recentHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-zinc-700">
                <thead className="bg-zinc-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Parametry
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Temperatury
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Czasy
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Energia
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Rezultat
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-zinc-800 divide-y divide-zinc-700">
                  {recentHistory.map((entry) => (
                    <tr key={entry.id} className="hover:bg-zinc-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {new Date(entry.timestamp).toLocaleDateString('pl-PL')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        {entry.thickness}mm, {entry.bendAngle}°, L:{entry.bendLength}mm
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        G:{entry.parameters.upperHeaterTemp}°C, D:{entry.parameters.lowerHeaterTemp}°C
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        Grz:{entry.parameters.heatingTime}s, Chł:{entry.parameters.coolingTime}s
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        {entry.energyUsed ? `${entry.energyUsed.toFixed(3)} kWh` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          entry.result === 'success' 
                            ? 'bg-green-900/50 text-green-300 border border-green-800'
                            : entry.result === 'partial'
                            ? 'bg-yellow-900/50 text-yellow-300 border border-yellow-800'
                            : 'bg-red-900/50 text-red-300 border border-red-800'
                        }`}>
                          {entry.result === 'success' ? 'Udany' : entry.result === 'partial' ? 'Częściowy' : 'Nieudany'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">
              Brak historii dla wybranych parametrów
            </p>
          )}
        </div>
      )}
      
      {/* Panel analizy defektów */}
      {showDefectAnalysis && (
        <div className="bg-zinc-800 rounded-lg shadow-xl p-6 border border-zinc-700">
          <h2 className="text-xl font-semibold mb-4 text-white">Analiza defektów</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Wybierz defekt do analizy
              </label>
              <select
                value={selectedDefect}
                onChange={(e) => setSelectedDefect(e.target.value)}
                className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">-- Wybierz defekt --</option>
                {BENDING_DEFECTS.map((defect) => (
                  <option key={defect.name} value={defect.name}>
                    {defect.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={analyzeDefect}
                disabled={!selectedDefect || !result}
                className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:bg-zinc-600 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <BookOpen className="w-4 h-4" />
                Analizuj i sugeruj poprawki
              </button>
            </div>
          </div>
          
          {/* Lista wszystkich defektów */}
          <div className="mt-6">
            <h3 className="font-semibold text-gray-200 mb-3">Baza defektów i rozwiązań</h3>
            <div className="space-y-3">
              {BENDING_DEFECTS.map((defect) => (
                <div key={defect.name} className="bg-zinc-700 border border-zinc-600 rounded-lg p-3 hover:bg-zinc-600 transition-colors">
                  <h4 className="font-semibold text-gray-100">{defect.name}</h4>
                  <p className="text-sm text-gray-300 mt-1">
                    <span className="font-medium text-gray-400">Przyczyna:</span> {defect.cause}
                  </p>
                  <p className="text-sm text-gray-300 mt-1">
                    <span className="font-medium text-gray-400">Rozwiązanie:</span> {defect.solution}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Statystyki materiału */}
      {selectedMaterialId && (
        <div className="bg-zinc-800 rounded-lg shadow-xl p-6 border border-zinc-700">
          <h2 className="text-xl font-semibold mb-4 text-white">
            Statystyki dla {findMaterial(selectedMaterialId)?.name}
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(() => {
              const stats = BendingHistory.getMaterialStatistics(selectedMaterialId);
              const avgEnergyCost = stats.averageEnergy * 0.65; // PLN/kWh
              return (
                <>
                  <div className="text-center bg-zinc-700/50 rounded-lg p-4">
                    <p className="text-3xl font-bold text-blue-400">{stats.totalProcesses}</p>
                    <p className="text-sm text-gray-400">Procesów</p>
                  </div>
                  <div className="text-center bg-zinc-700/50 rounded-lg p-4">
                    <p className="text-3xl font-bold text-green-400">{stats.successRate}%</p>
                    <p className="text-sm text-gray-400">Skuteczność</p>
                  </div>
                  <div className="text-center bg-zinc-700/50 rounded-lg p-4">
                    <p className="text-3xl font-bold text-yellow-400">{stats.averageCycleTime}s</p>
                    <p className="text-sm text-gray-400">Śr. czas cyklu</p>
                  </div>
                  <div className="text-center bg-zinc-700/50 rounded-lg p-4">
                    <p className="text-3xl font-bold text-purple-400">{avgEnergyCost.toFixed(2)} PLN</p>
                    <p className="text-sm text-gray-400">Śr. koszt energii</p>
                  </div>
                </>
              );
            })()}
          </div>
          
          {/* Podsumowanie kosztów miesięcznych */}
          {energyStats && (
            <div className="mt-4 p-4 bg-zinc-700/30 rounded-lg">
              <h3 className="font-semibold text-gray-200 mb-2">Prognoza miesięczna (22 dni robocze)</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-400">Produkcja</p>
                  <p className="font-bold text-blue-400">{energyStats.dailyProduction * 22} szt</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Zużycie energii</p>
                  <p className="font-bold text-yellow-400">{(energyStats.totalEnergy * energyStats.dailyProduction * 22).toFixed(0)} kWh</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Koszt energii</p>
                  <p className="font-bold text-red-400">{(energyStats.dailyEnergyCost * 22).toFixed(2)} PLN</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BendingCalculator;